import { z } from "zod";
import { ZodSchema } from "@validation/utils/schemaUtils";
import { locationSelectOptionSchema } from "view/locations/utils/location.schema";
import { ExpectedCargoSchema } from "view/cargos/utils/schema";
import { shipSelectOptionSchema } from "view/ships/utils/ship.schema";
import { CreateOrExistingPurchaseCharterSchema } from "view/charters/utils/charter.schema";
import { DateUtils } from "utils/DateUtils";
import { intl } from "LocaleWrapper";
import { determineCharterValidity } from "view/charters/utils/CharterUtils";
import { relationSelectOptionSchema } from "view/relations/utils/relation.schema";
import { CharterSubtype, CharterType } from "__generated__/enums";

export const voyageCallSchema = z.object({
	id: z.number().nullable(),
	location: ZodSchema.requiredField(locationSelectOptionSchema),
	atn: z.number().nullable(),
	dtn: z.number().nullable(),
	ata: z.number().nullable(),
	atd: z.number().nullable(),
	ateo: z.number().nullable(),
	atso: z.number().nullable(),
	eta: z.number().nullable(),
	etd: z.number().nullable(),
	eteo: z.number().nullable(),
	etso: z.number().nullable(),
	pta: ZodSchema.requiredField(z.number()),
	ptd: ZodSchema.requiredField(z.number()),
});

export type VoyageCallSchemaInput = z.input<typeof voyageCallSchema>;
export type VoyageCallSchemaOutput = z.output<typeof voyageCallSchema>;

export const voyageSchema = z.object({
	id: z.number().nullable(),
	voyageNumber: z.string(),
	ship: ZodSchema.requiredField(shipSelectOptionSchema),
	creditor: relationSelectOptionSchema.nullable(),
	start: z.number().nullable(),
	end: z.number().nullable(),
	schedule: z.object({ id: z.number(), schedule: z.string() }).nullable(),
	isBallast: z.boolean(),
	scopeIn: z.object({
		desired: z.number().nullable(),
		planned: z.number().nullable(),
		expected: z.number().nullable(),
		actual: z.number().nullable(),
		expectedLocation: locationSelectOptionSchema.nullable(),
		plannedLocation: locationSelectOptionSchema.nullable(),
		actualLocation: locationSelectOptionSchema.nullable(),
	}),
	scopeOut: z.object({
		desired: z.number().nullable(),
		planned: z.number().nullable(),
		expected: z.number().nullable(),
		actual: z.number().nullable(),
		expectedLocation: locationSelectOptionSchema.nullable(),
		plannedLocation: locationSelectOptionSchema.nullable(),
		actualLocation: locationSelectOptionSchema.nullable(),
	}),
	calls: z.array(
		voyageCallSchema.extend({
			expectedCargos: z.array(ExpectedCargoSchema),
		}),
	),
	purchaseCharters: z.array(z.object({
		id: z.number(),
		type: z.enum(CharterType),
		subtype: z.enum(CharterSubtype),
		charterNumber: z.string(),
		creditor: relationSelectOptionSchema.nullable(),
		debtor: relationSelectOptionSchema.nullable(),
		validFrom: z.number().nullable(),
		validUntil: z.number().nullable(),
		shortNotice: z.boolean(),
	})).nullable(),
});

export type VoyageSchemaInput = z.input<typeof voyageSchema>;
export type VoyageSchemaOutput = z.output<typeof voyageSchema>;

export const voyageAddDialogSchema = voyageSchema.pick({
	voyageNumber: true,
	ship: true,
	isBallast: true,
}).extend({
	calls: z.array(voyageCallSchema.pick({
		id: true,
		location: true,
		pta: true,
		ptd: true,
	}).extend({
		expectedCargos: z.array(ExpectedCargoSchema),
	}).refine(call => {
		if (!call.pta || !call.ptd || call.pta <= 0 || call.ptd <= 0) {
			return true;
		}
		return call.ptd >= call.pta;
	}, {
		message: intl.formatMessage({ id: "PTD_BEFORE_PTA" }),
		path: [ "ptd" ],
	})),
	...CreateOrExistingPurchaseCharterSchema.shape,
}).superRefine((val, ctx) => {
	const [ validFrom, validUntil ] = determineCharterValidity(val.charter);

	if (validFrom && validUntil) {
		val.calls.forEach((call, index) => {
			if (call.pta < (DateUtils.getTimestamp(validFrom) ?? 0)) {
				ctx.addIssue({
					code: "custom",
					path: [ "calls", index, "pta" ],
					message: intl.formatMessage({ id: "CALL_PTA_BEFORE_CHARTER_VALID_FROM" }),
				});
			}
			if (call.ptd > (DateUtils.getTimestamp(validUntil) ?? Infinity)) {
				ctx.addIssue({
					code: "custom",
					path: [ "calls", index, "ptd" ],
					message: intl.formatMessage({ id: "CALL_PTD_AFTER_CHARTER_VALID_UNTIL" }),
				});
			}
		});
	}
});

export type VoyageAddDialogSchemaInput = z.input<typeof voyageAddDialogSchema>;
export type VoyageAddDialogSchemaOutput = z.output<typeof voyageAddDialogSchema>;

export const rescheduledVoyageFormSchema = z.object({
	calls: z.array(z.object({
		id: z.number(),
		location: ZodSchema.requiredField(locationSelectOptionSchema),
		pta: ZodSchema.requiredField(z.number()),
		ptd: ZodSchema.requiredField(z.number()),
	}).transform(({ location, ...rest }) => ({
		...rest,
		locationId: location.id,
	}))),
}).superRefine((data, ctx) => {
	data.calls.forEach((call, index) => {
		if (call.pta > call.ptd) {
			ctx.addIssue({
				code: "custom",
				message: intl.formatMessage({ id: "PTD_BEFORE_PTA" }),
				path: [ "calls", index, "ptd" ],
			});
		}
	});
});

export type RescheduledVoyageFormSchemaInput = z.input<typeof rescheduledVoyageFormSchema>;
export type RescheduledVoyageFormSchemaOutput = z.output<typeof rescheduledVoyageFormSchema>;

export const updateVoyageScheduleSchema = z.object({
	schedule: ZodSchema.requiredField(z.object({ id: z.number(), schedule: z.string() })),
});

export type UpdateVoyageScheduleSchemaInput = z.input<typeof updateVoyageScheduleSchema>;
export type UpdateVoyageScheduleSchemaOutput = z.output<typeof updateVoyageScheduleSchema>;

export const voyagePartialEditSchema = voyageSchema.pick({
	id: true,
	voyageNumber: true,
	ship: true,
	schedule: true,
	isBallast: true,
}).extend({
	references: z.array(z.object({
		id: z.number(),
		customerReference: z.string(),
		charterNumber: z.string(),
	})),
});

export type VoyagePartialEditSchemaInput = z.input<typeof voyagePartialEditSchema>;
export type VoyagePartialEditSchemaOutput = z.output<typeof voyagePartialEditSchema>;
